<template>
  <div class="ledger-page">
    <b-card
      v-if="canCreate"
      class="rounded-lg"
    >
      <h4 class="mb-50">
        {{ $t('ledger_payroll_new') }}
      </h4>
      <p class="text-muted">
        {{ $t('ledger_payroll_create_hint') }}
      </p>
      <b-row>
        <b-col
          cols="6"
          md="3"
        >
          <b-form-group :label="$t('ledger_payroll_year')">
            <b-form-input
              v-model.number="form.year"
              type="number"
              min="2000"
              max="2100"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          md="3"
        >
          <b-form-group :label="$t('ledger_payroll_month')">
            <b-form-select
              v-model.number="form.month"
              :options="monthOptions"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="4"
          class="d-flex align-items-end mb-1"
        >
          <b-button
            variant="primary"
            class="rounded-pill"
            block
            :disabled="creating || !form.year || !form.month"
            @click="confirmCreate"
          >
            <b-spinner
              v-if="creating"
              small
              class="mr-50"
            />
            {{ $t('ledger_payroll_create') }}
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <b-card class="rounded-lg">
      <h4 class="mb-1">
        {{ $t('ledger_payroll_title') }}
      </h4>

      <div
        v-if="loading"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
      </div>

      <div
        v-else-if="!rows.length"
        class="text-center text-muted py-4"
      >
        {{ $t('ledger_payroll_empty') }}
      </div>

      <div v-else>
        <div class="d-none d-md-block">
          <b-table
            :items="rows"
            :fields="fields"
            small
            hover
            responsive
            class="ledger-dense-table"
          >
            <template #cell(period)="data">
              <b-link :to="showRoute(data.item)">
                {{ periodLabel(data.item) }}
              </b-link>
            </template>
            <template #cell(status)="data">
              <b-badge :variant="statusBadgeVariant(statusValue(data.item))">
                {{ statusText(data.item) }}
              </b-badge>
            </template>
            <template #cell(net)="data">
              <span class="ledger-num">{{ money(netOf(data.item)) }}</span>
            </template>
            <template #cell(actions)="data">
              <b-link :to="showRoute(data.item)">
                {{ $t('ledger_payroll_open') }}
              </b-link>
            </template>
          </b-table>
        </div>

        <div class="d-md-none">
          <article
            v-for="item in rows"
            :key="item.id"
            class="ledger-payroll-card"
            @click="$router.push(showRoute(item))"
          >
            <div class="d-flex justify-content-between align-items-center">
              <strong>{{ periodLabel(item) }}</strong>
              <b-badge :variant="statusBadgeVariant(statusValue(item))">
                {{ statusText(item) }}
              </b-badge>
            </div>
            <div class="ledger-num mt-50">
              {{ $t('ledger_payroll_net') }} {{ money(netOf(item)) }}
            </div>
          </article>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormGroup, BFormInput, BFormSelect, BButton, BTable, BBadge, BLink, BSpinner,
} from 'bootstrap-vue'
import Swal from 'sweetalert2'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getPayrollRuns, createPayrollRun } from '@/api/accounting/payrollRuns'
import {
  listFrom, oneFrom, enumValue, enumLabel, formatLedgerMoney, statusBadgeVariant, apiErrorMessage,
} from '@/utils/ledger'

export default {
  name: 'PayrollRunsList',
  components: {
    BCard, BRow, BCol, BFormGroup, BFormInput, BFormSelect, BButton, BTable, BBadge, BLink, BSpinner,
  },
  data() {
    const now = new Date()
    return {
      loading: false,
      creating: false,
      rows: [],
      form: {
        year: now.getFullYear(),
        month: now.getMonth() + 1,
      },
    }
  },
  computed: {
    canCreate() {
      return this.hasPermission('create payroll')
    },
    monthOptions() {
      return Array.from({ length: 12 }, (_, index) => ({
        value: index + 1,
        text: this.monthName(index + 1),
      }))
    },
    fields() {
      return [
        { key: 'period', label: this.$t('ledger_payroll_period') },
        { key: 'status', label: this.$t('ledger_status') },
        { key: 'net', label: this.$t('ledger_payroll_net') },
        { key: 'actions', label: this.$t('ledger_actions') },
      ]
    },
  },
  mounted() {
    this.loadRuns()
  },
  methods: {
    statusBadgeVariant,
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    monthName(month) {
      const loc = this.$i18n.locale === 'en' ? 'en-EG' : 'ar-EG'
      return new Intl.DateTimeFormat(loc, { month: 'long' }).format(new Date(2020, month - 1, 1))
    },
    periodLabel(run) {
      return `${this.monthName(run.month)} ${run.year}`
    },
    statusValue(run) {
      return enumValue(run.status)
    },
    statusText(run) {
      return enumLabel(run.status, this.$i18n.locale)
    },
    netOf(run) {
      return run.totals && run.totals.net
    },
    showRoute(run) {
      return { name: 'ledger-payroll-view', params: { id: run.id } }
    },
    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon',
          variant,
        },
      })
    },
    async loadRuns() {
      this.loading = true
      try {
        const res = await getPayrollRuns()
        this.rows = listFrom(res).slice().sort((a, b) => {
          if (a.year !== b.year) return b.year - a.year
          return b.month - a.month
        })
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.loading = false
      }
    },
    async confirmCreate() {
      if (!this.canCreate || this.creating) return
      const result = await Swal.fire({
        text: this.$t('ledger_payroll_create_confirm'),
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_payroll_create'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      this.creating = true
      try {
        const res = await createPayrollRun({
          year: Number(this.form.year),
          month: Number(this.form.month),
        })
        const run = oneFrom(res)
        this.toast(this.$t('ledger_payroll_created'), 'success')
        if (run && run.id) {
          this.$router.push({ name: 'ledger-payroll-view', params: { id: run.id } })
          return
        }
        await this.loadRuns()
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.creating = false
      }
    },
  },
}
</script>

<style scoped>
.ledger-num {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.ledger-payroll-card {
  border-bottom: 1px solid #eee;
  padding: 0.85rem 0.15rem;
  cursor: pointer;
}
</style>
